export interface ParcelDetails {
  reid: string;
  bedrooms: number | null;
  fullBaths: number | null;
  halfBaths: number | null;
  yearBuilt: number | null;
  photoUrl: string | null;
  sourceUpdatedAt: string | null;
}

// Cache in-flight and completed lookups per session so repeated popup opens
// do not re-hit the Pages Function for the same parcel.
const parcelDetailsCache = new Map<string, Promise<ParcelDetails | null>>();

// The Pages Function is intentionally narrow, so keep the client parser
// defensive and tolerate missing or partially populated fields.
function normalizeDetails(value: unknown): ParcelDetails | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;

  const record = value as Record<string, unknown>;
  return {
    reid: typeof record.reid === 'string' ? record.reid : '',
    bedrooms: typeof record.bedrooms === 'number' ? record.bedrooms : null,
    fullBaths: typeof record.fullBaths === 'number' ? record.fullBaths : null,
    halfBaths: typeof record.halfBaths === 'number' ? record.halfBaths : null,
    yearBuilt: typeof record.yearBuilt === 'number' ? record.yearBuilt : null,
    photoUrl: typeof record.photoUrl === 'string' ? record.photoUrl : null,
    sourceUpdatedAt: typeof record.sourceUpdatedAt === 'string' ? record.sourceUpdatedAt : null,
  };
}

/**
 * Render the compact bedroom/bath summary used in the popup.
 * Returns `N/A` when the parcel lookup did not provide usable data.
 */
export function formatBedroomBathSummary(details: ParcelDetails | null): string {
  if (!details) return 'N/A';

  const hasBedroom = details.bedrooms !== null;
  const hasBath = details.fullBaths !== null || details.halfBaths !== null;
  if (!hasBedroom && !hasBath) return 'N/A';

  const bedLabel = hasBedroom ? `${details.bedrooms} bed` : 'N/A bed';
  const bathCount = (details.fullBaths ?? 0) + (details.halfBaths ?? 0) * 0.5;
  const bathLabel = hasBath ? `${Number.isInteger(bathCount) ? bathCount.toFixed(0) : bathCount.toFixed(1)} bath` : 'N/A bath';

  return `${bedLabel} / ${bathLabel}`;
}

/**
 * Preserve the popup's `N/A` fallback for unknown build years.
 */
export function formatYearBuilt(details: ParcelDetails | null): string {
  return details?.yearBuilt ? String(details.yearBuilt) : 'N/A';
}

/**
 * Fetch parcel enrichment through the local Pages Function and memoize the
 * request so the UI can reopen popups without duplicating network traffic.
 */
export async function fetchParcelDetails(reid: string): Promise<ParcelDetails | null> {
  const cacheKey = reid.trim();
  if (!cacheKey) return null;

  let cached = parcelDetailsCache.get(cacheKey);
  if (!cached) {
    cached = fetch(`/api/parcel-details?reid=${encodeURIComponent(cacheKey)}`)
      .then(async (response) => {
        if (!response.ok) return null;
        return normalizeDetails(await response.json());
      })
      .catch(() => null)
      .then((details) => {
        // Only keep successful parses in the cache; failures are retried on the
        // next open so transient API issues do not get stuck for the session.
        if (details === null) parcelDetailsCache.delete(cacheKey);
        return details;
      });
    parcelDetailsCache.set(cacheKey, cached);
  }

  return cached;
}
